import { createFileRoute, Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { SiteFooter } from "@/components/site-footer";
import icon from "@/assets/easy-lover/easy-lover-icon.png.asset.json";
import shareArtwork from "@/assets/easy-lover/easy-lover-share.jpg.asset.json";
import wideArtwork from "@/assets/easy-lover/easy-lover-wide-artwork.png.asset.json";

const TITLE = "Easy Lover Privacy Policy | the Vibing Company";
const DESCRIPTION =
  "How the Easy Lover Chrome extension processes the Lovable editor locally, stores toolbar preferences, and protects your data.";
const CANONICAL = "https://vibing.inc/easy-lover/privacy";
const OG_IMAGE = `https://vibing.inc${shareArtwork.url}`;

export const Route = createFileRoute("/easy-lover/privacy")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:alt", content: "Easy Lover toolbar artwork" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      { rel: "alternate", type: "text/markdown", href: "https://vibing.inc/md/easy-lover/privacy" },
    ],
  }),
  component: EasyLoverPrivacyPage,
});

function PrivacySection({ number, title, children }: { number: string; title: string; children: ReactNode }) {
  return (
    <section className="grid gap-4 border-t border-border py-10 md:grid-cols-12 md:gap-8">
      <p className="font-mono text-xs text-muted-foreground md:col-span-1">{number}</p>
      <div className="md:col-span-8 md:col-start-4">
        <h2 className="text-2xl font-medium">{title}</h2>
        <div className="mt-5 space-y-4 leading-relaxed text-muted-foreground">{children}</div>
      </div>
    </section>
  );
}

function EasyLoverPrivacyPage() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" tabIndex={-1}>
        <header className="mx-auto max-w-[1100px] px-6 pb-16 pt-[clamp(9rem,18vh,13rem)] md:px-10 md:pb-24">
          <Link to="/easy-lover" className="inline-flex items-center gap-3 text-sm font-medium">
            <img src={icon.url} alt="" width={128} height={128} className="h-10 w-10 rounded-md" />
            Easy Lover
          </Link>
          <h1 className="mt-10 font-display text-[clamp(2.5rem,6vw,5rem)] leading-none">Extension privacy policy</h1>
          <p className="mt-8 max-w-[64ch] text-lg leading-relaxed text-muted-foreground md:text-xl">
            This policy explains what the Easy Lover Chrome extension reads, changes, and stores.
            It applies to the extension itself. The website at vibing.inc has a separate privacy policy.
          </p>
          <p className="mt-5 text-sm text-muted-foreground">Last updated: September 14, 2026</p>
        </header>

        <div className="mx-auto max-w-[1100px] px-6 pb-20 md:px-10 md:pb-32">
          <PrivacySection number="01" title="What the extension processes">
            <p>
              Easy Lover processes the Lovable editor interface locally in your browser. This
              includes toolbar labels, the selected page state, styles, and icons. It uses that
              information to adapt the editor navigation and to activate existing Lovable controls
              when you request an action.
            </p>
          </PrivacySection>

          <PrivacySection number="02" title="What is stored">
            <p>
              The extension saves your pinned pages, page order, label preferences, GitHub shortcut
              visibility, and enabled state in <code className="font-mono text-foreground">chrome.storage.local</code>.
              These settings stay in the Chrome profile where you configured them and do not sync
              across devices.
            </p>
          </PrivacySection>

          <PrivacySection number="03" title="Where the extension runs">
            <p>
              Site access is limited to <code className="font-mono text-foreground">https://lovable.dev/*</code> and{" "}
              <code className="font-mono text-foreground">https://www.lovable.dev/*</code> so Easy Lover can keep working
              as you move into projects. It adapts recognized editor toolbars and does not run inside
              project preview frames.
            </p>
          </PrivacySection>

          <PrivacySection number="04" title="Data transmission and use">
            <p>
              Easy Lover has no telemetry or server of its own. It does not transmit user data to
              the developer or external services. It does not sell data, use data for advertising,
              or use data for unrelated purposes.
            </p>
          </PrivacySection>

          <PrivacySection number="05" title="Extension code">
            <p>
              All executable extension code ships inside the installed package. Easy Lover does not
              download or execute remote JavaScript or WebAssembly.
            </p>
          </PrivacySection>

          <PrivacySection number="06" title="Lovable and GitHub controls">
            <p>
              Lovable's own page may make its normal requests when you use native controls. The
              optional GitHub shortcut only opens Lovable's GitHub connector settings. Lovable and
              GitHub handle account connection and synchronization through their own interfaces and
              policies. Easy Lover does not authorize an account or change sync settings automatically.
            </p>
          </PrivacySection>

          <PrivacySection number="07" title="Your controls and removal">
            <p>
              You can change label, shortcut, order, and enabled settings or restore the defaults in
              the Easy Lover popup. Removing the extension removes its locally stored settings from
              Chrome.
            </p>
          </PrivacySection>

          <PrivacySection number="08" title="Publisher and contact">
            <p>
              Easy Lover is published by the Vibing Company, Jonathan Osburg, St.-Klara-Str. 7A,
              82377 Penzberg, Germany.
            </p>
            <p>
              Privacy and support questions can be sent to{" "}
              <a href="mailto:jon@vibing.inc" className="text-foreground underline underline-offset-4">jon@vibing.inc</a>{" "}
              or through the <Link to="/contact" className="text-foreground underline underline-offset-4">contact page</Link>.
            </p>
          </PrivacySection>

          <PrivacySection number="09" title="Website privacy">
            <p>
              This page covers Easy Lover. For information about data handling on vibing.inc, read
              the <Link to="/privacy" className="text-foreground underline underline-offset-4">website privacy policy</Link>.
            </p>
          </PrivacySection>

          <div className="overflow-hidden rounded-lg border border-border">
            <img
              src={wideArtwork.url}
              alt="Easy Lover artwork showing the customizable toolbar"
              width={1983}
              height={793}
              loading="lazy"
              decoding="async"
              className="h-auto w-full"
            />
          </div>
          <p className="mt-8">
            <Link to="/easy-lover" className="font-medium underline underline-offset-4">Back to Easy Lover</Link>
          </p>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
